import { PromoDisplay } from './promoDisplay';

type PromoMessageContext = {
  promoName: string;
  remainingLabel: string;
  display: PromoDisplay;
  playerFacingConfig?: any;
};

export type PromoModalCopy = {
  eyebrow: string;
  title: string;
  remainingMessage: string;
  remainingMessageNotQualified?: string;
  remainingMessageQualified?: string;
  remainingMessageBonusAchieved?: string;
  statusLabel: string;
  statusLabelNotQualified?: string;
  statusLabelCurrentWeekQualified?: string;
  statusLabelWeeklyPathQualified?: string;
  statusLabelTotalFallbackQualified?: string;
  helperMessage: string;
  primaryAction: string;
  secondaryAction: string;
  footer: string;
};

export type PromoInlineCopy = {
  title: string;
  promoBadgeLabel: string;
  statusLabel?: string;
  statusLabelNotQualified?: string;
  statusLabelQualified?: string;
  statusLabelBonusAchieved?: string;
};

export type PromoAccountBannerCopy = {
  title: string;
  body: string;
  bodyNotQualified: string;
  bodyQualified: string;
  bodyBonusAchieved: string;
  actionLabel: string;
};

const defaultFooter = 'Restrictions apply. Contact support for details.';

const copyByFormat: Record<string, (context: PromoMessageContext) => PromoModalCopy> = {
  weekly_qualification_freeroll: ({ promoName, remainingLabel }) => ({
    eyebrow: 'Weekly Freeroll',
    title: `Earn your seat at ${promoName}`,
    remainingMessage: `You have ${remainingLabel} remaining this week. Keep the streak going.`,
    statusLabel: 'Not qualified yet',
    helperMessage:
      'Each week has its own qualification window. View Details for weekly achievements and reward tiers.',
    primaryAction: 'View Details',
    secondaryAction: 'Play Now',
    footer: defaultFooter
  }),

  tournament_tier_qualification: ({ promoName, remainingLabel }) => ({
    eyebrow: 'Tier Qualification',
    title: `Climb the tiers for ${promoName}`,
    remainingMessage: `${remainingLabel} remaining to the next qualification tier.`,
    statusLabel: 'Not qualified yet',
    helperMessage:
      'Your total tracked hours determine the highest tournament tier you qualify for.',
    primaryAction: 'View Tiers',
    secondaryAction: 'Play Now',
    footer: defaultFooter
  }),

  hybrid_freeroll_qualification: ({ promoName, remainingLabel }) => ({
    eyebrow: 'Hybrid Freeroll',
    title: `Build your freeroll stack for ${promoName}`,
    remainingMessage: `You have ${remainingLabel} remaining on the weekly path.`,
    remainingMessageNotQualified: `You have ${remainingLabel} remaining this week. Grind your hours and grab your seat!`,
    remainingMessageQualified: `You're qualified for ${promoName}. Keep playing to build your bonus stack.`,
    remainingMessageBonusAchieved: `You've earned {{earnedLabel}}. Keep playing to protect your stack.`,
    statusLabel: 'Not qualified yet',
    statusLabelNotQualified: 'Not qualified yet',
    statusLabelCurrentWeekQualified: 'Qualified this week',
    statusLabelWeeklyPathQualified: 'Qualified by weekly path',
    statusLabelTotalFallbackQualified: 'Qualified by total-hours fallback',
    helperMessage:
      'Qualify through weekly play or the total-hours fallback. View Details to compare both paths.',
    primaryAction: 'View Details',
    secondaryAction: 'Play Now',
    footer: defaultFooter
  }),

  weekly_timeback_credit: ({ promoName, remainingLabel }) => ({
    eyebrow: 'Weekly Timeback',
    title: `Track your timeback for ${promoName}`,
    remainingMessage: `${remainingLabel} in weekly net fees remaining to the next refund tier.`,
    statusLabel: 'Not qualified yet',
    helperMessage:
      'Timeback is based on weekly fee tiers and settles according to the promotion rules.',
    primaryAction: 'View Tiers',
    secondaryAction: 'Play Now',
    footer: defaultFooter
  }),

  // Weekly Cash Back defaults (EN-11291). Admin's player-facing config overrides them.
  weekly_cash_back: ({ promoName, remainingLabel }) => ({
    eyebrow: 'Weekly Cash Back',
    title: `Earn cash back at ${promoName}`,
    remainingMessage: `${remainingLabel} to your next cash back tier this week.`,
    remainingMessageNotQualified: `${remainingLabel} to your first cash back tier this week.`,
    remainingMessageQualified: `Cash back earned this week. ${remainingLabel} to the top tier.`,
    remainingMessageBonusAchieved:
      'Top tier earned this week. Hours reset when the next window opens.',
    statusLabel: 'Not qualified yet',
    statusLabelNotQualified: 'Not qualified yet',
    statusLabelCurrentWeekQualified: '{{earnedLabel}} earned',
    // Each state message carries what the player needs next, so there is no separate
    // helper line to go stale once a tier is earned (demo 2026-10-28).
    helperMessage: '',
    // primaryAction labels the details button, secondaryAction the play button (field names
    // predate the layout swap; the modal now leads with play).
    primaryAction: 'View progress',
    secondaryAction: 'Play now',
    footer: defaultFooter
  }),

  legacy: ({ promoName }) => ({
    eyebrow: 'Promotion',
    title: promoName,
    remainingMessage: 'Your progress is being tracked for this promotion.',
    statusLabel: 'In progress',
    helperMessage: 'View Details to see current goals, rewards, and progress.',
    primaryAction: 'View Details',
    secondaryAction: 'Play Now',
    footer: defaultFooter
  })
};

export function getPromoModalCopy(context: PromoMessageContext): PromoModalCopy {
  const resolver = copyByFormat[context.display.format] || copyByFormat.legacy;
  const termsAndConditions = context.playerFacingConfig?.termsAndConditions || defaultFooter;
  const tokens = {
    ...tokenContext(context),
    termsAndConditions
  };

  const copy = applyConfiguredCopy(
    {
      ...resolver(context),
      footer: termsAndConditions
    },
    context.playerFacingConfig?.copy?.entryModal,
    tokens
  );

  return {
    ...copy,
    remainingMessage: getEntryRemainingMessage(context, copy),
    statusLabel: getEntryStatusLabel(context, copy),
    footer: interpolate(termsAndConditions, tokens)
  };
}

/** The three-state tier position for formats that expose one: hybrid freerolls through
 *  hybridDetails.state, two-tier cash back through tierState (EN-11291). */
const getTierState = (
  context: PromoMessageContext
): 'notQualified' | 'qualified' | 'bonusAchieved' | null =>
  context.display.hybridDetails?.state || context.display.tierState || null;

/** Cash back with no hours yet this window. "Play 15h more" would read as if the player had
 *  started, so this state shows the base message (Admin's `remainingMessage` / `body`). */
const hasNotStartedCashBack = (context: PromoMessageContext): boolean =>
  context.display.format === 'weekly_cash_back' &&
  context.display.tierState === 'notQualified' &&
  context.display.progressValue <= 0;

const getEntryRemainingMessage = (context: PromoMessageContext, copy: PromoModalCopy): string => {
  const state = getTierState(context);
  if (!state || hasNotStartedCashBack(context)) {
    return copy.remainingMessage;
  }

  if (state === 'bonusAchieved') {
    return copy.remainingMessageBonusAchieved || copy.remainingMessage;
  }
  if (state === 'qualified') {
    return copy.remainingMessageQualified || copy.remainingMessage;
  }

  return copy.remainingMessageNotQualified || copy.remainingMessage;
};

const getEntryStatusLabel = (context: PromoMessageContext, copy: PromoModalCopy): string => {
  if (context.display.format !== 'hybrid_freeroll_qualification') {
    if (
      context.display.tierState === 'qualified' ||
      context.display.tierState === 'bonusAchieved'
    ) {
      return (
        copy.statusLabelCurrentWeekQualified || copy.statusLabel || context.display.statusLabel
      );
    }
    if (context.display.tierState === 'notQualified') {
      return copy.statusLabelNotQualified || copy.statusLabel || context.display.statusLabel;
    }
    return copy.statusLabel || context.display.statusLabel;
  }

  const statusKey = context.display.hybridDetails?.entryStatusKey || 'notQualified';
  if (statusKey === 'totalFallbackQualified') {
    return (
      copy.statusLabelTotalFallbackQualified || copy.statusLabel || context.display.statusLabel
    );
  }
  if (statusKey === 'currentWeekQualified') {
    return copy.statusLabelCurrentWeekQualified || copy.statusLabel || context.display.statusLabel;
  }
  if (statusKey === 'weeklyPathQualified') {
    return copy.statusLabelWeeklyPathQualified || copy.statusLabel || context.display.statusLabel;
  }

  return copy.statusLabelNotQualified || copy.statusLabel || context.display.statusLabel;
};

const inlineCopyByFormat: Record<string, (context: PromoMessageContext) => PromoInlineCopy> = {
  weekly_qualification_freeroll: ({ display }) => ({
    title: '{{promoName}}',
    promoBadgeLabel: display.earnedLabel === 'Not qualified' ? 'Promo' : display.earnedLabel
  }),

  tournament_tier_qualification: ({ display }) => ({
    title: '{{promoName}}',
    promoBadgeLabel: display.earnedLabel === 'Not qualified' ? 'Tier Up' : display.earnedLabel
  }),

  hybrid_freeroll_qualification: ({ display }) => ({
    title: '{{promoName}}',
    statusLabelNotQualified: 'Left',
    statusLabelQualified: 'Qualified',
    statusLabelBonusAchieved: 'Bonus stack earned',
    promoBadgeLabel: display.earnedLabel || 'Promo'
  }),

  weekly_timeback_credit: ({ display }) => ({
    title: '{{promoName}}',
    promoBadgeLabel: display.earnedLabel === 'No timeback yet' ? 'Timeback' : display.earnedLabel
  }),

  // The lobby strip shows the hours ("16.2h / 30h") and adds this status only once $20 is
  // earned ("· $20 earned"); the prize ticket carries the amount still to win.
  weekly_cash_back: ({ display }) => ({
    title: '{{promoName}}',
    statusLabelNotQualified: '{{remainingLabel}} to next tier',
    statusLabelQualified: '{{earnedLabel}} earned',
    statusLabelBonusAchieved: '{{earnedLabel}} earned',
    promoBadgeLabel: display.earnedLabel === 'Not qualified' ? 'Cash Back' : display.earnedLabel
  }),

  legacy: ({ display }) => ({
    title: '{{promoName}}',
    promoBadgeLabel: display.earnedLabel || display.formatLabel
  })
};

export function getPromoInlineCopy(context: PromoMessageContext): PromoInlineCopy {
  const resolver = inlineCopyByFormat[context.display.format] || inlineCopyByFormat.legacy;
  const copy = applyConfiguredCopy(
    resolver(context),
    context.playerFacingConfig?.copy?.inlineBar,
    tokenContext(context)
  );

  return resolveCurrentWeekInlineCopy(context, copy);
}

export function getPromoGameIndicatorCopy(context: PromoMessageContext): PromoInlineCopy {
  const resolver = inlineCopyByFormat[context.display.format] || inlineCopyByFormat.legacy;
  const baseCopy = applyConfiguredCopy(
    resolver(context),
    context.playerFacingConfig?.copy?.inlineBar,
    tokenContext(context)
  );

  const copy = applyConfiguredCopy(
    {
      ...baseCopy,
      statusLabelNotQualified: 'Left',
      statusLabelQualified: 'Done',
      statusLabelBonusAchieved: 'Done'
    },
    context.playerFacingConfig?.copy?.gameIndicator,
    tokenContext(context)
  );

  return resolveGameIndicatorCopy(context, copy);
}

const resolveGameIndicatorCopy = (
  context: PromoMessageContext,
  copy: PromoInlineCopy
): PromoInlineCopy => {
  const state = getTierState(context);
  if (!state) {
    return copy;
  }

  const currentWeekQualified =
    context.display.hybridDetails?.currentWeekQualified === true ||
    (context.display.tierState !== undefined && state !== 'notQualified');

  if (!currentWeekQualified) {
    return {
      ...copy,
      statusLabel: copy.statusLabelNotQualified || copy.statusLabel || 'Left'
    };
  }

  if (state === 'bonusAchieved') {
    return {
      ...copy,
      statusLabel: copy.statusLabelBonusAchieved || copy.statusLabel || 'Done'
    };
  }

  return {
    ...copy,
    statusLabel: copy.statusLabelQualified || copy.statusLabel || 'Done'
  };
};

const resolveCurrentWeekInlineCopy = (
  context: PromoMessageContext,
  copy: PromoInlineCopy
): PromoInlineCopy => {
  const state = getTierState(context);
  if (!state) {
    return copy;
  }

  const currentWeekQualified =
    context.display.hybridDetails?.currentWeekQualified === true ||
    (context.display.tierState !== undefined && state !== 'notQualified');

  if (!currentWeekQualified) {
    return {
      ...copy,
      statusLabel: copy.statusLabelNotQualified || copy.statusLabel || 'Left'
    };
  }

  if (state === 'bonusAchieved') {
    return {
      ...copy,
      statusLabel: copy.statusLabelBonusAchieved || copy.statusLabel || 'Bonus stack earned'
    };
  }

  return {
    ...copy,
    statusLabel: copy.statusLabelQualified || copy.statusLabel || 'Qualified'
  };
};

export function getPromoAccountBannerCopy(context: PromoMessageContext): PromoAccountBannerCopy {
  const configured = context.playerFacingConfig?.copy?.accountPanelBanner;
  const state = getAccountBannerState(context);
  const bodyField =
    state === 'bonusAchieved'
      ? 'bodyBonusAchieved'
      : state === 'qualified'
      ? 'bodyQualified'
      : 'bodyNotQualified';
  const defaults = accountBannerDefaultsByFormat(context);
  const copy = applyConfiguredCopy(
    defaults,
    configured
      ? {
          ...configured,
          bodyNotQualified: configured.bodyNotQualified || configured.body
        }
      : undefined,
    tokenContext(context)
  );

  return {
    ...copy,
    body:
      hasNotStartedCashBack(context) && configured?.body
        ? interpolate(configured.body, tokenContext(context))
        : copy[bodyField]
  };
}

const getAccountBannerState = (
  context: PromoMessageContext
): 'notQualified' | 'qualified' | 'bonusAchieved' => {
  if (context.display.hybridDetails?.state) {
    return context.display.hybridDetails.state;
  }
  if (context.display.tierState) {
    return context.display.tierState;
  }

  return context.display.progressPct >= 100 ? 'qualified' : 'notQualified';
};

const accountBannerDefaultsByFormat = (
  context: PromoMessageContext
): Omit<PromoAccountBannerCopy, 'body'> => {
  if (context.display.format === 'hybrid_freeroll_qualification') {
    return {
      title: context.promoName || 'Freeroll Progress',
      bodyNotQualified:
        'You have {{remainingLabel}} left this week. Keep playing to grab your seat.',
      bodyQualified:
        "You're qualified for {{promoName}}. View details to check your seat and stack.",
      bodyBonusAchieved: 'Bonus stack earned. View details to see your {{bonusStack}} boost.',
      actionLabel: 'View progress'
    };
  }

  if (context.display.format === 'weekly_cash_back') {
    return {
      title: context.promoName || 'Weekly Cash Back',
      bodyNotQualified: 'Play {{remainingLabel}} more to earn cash back this week.',
      bodyQualified:
        '{{earnedLabel}} earned this week. Play {{remainingLabel}} more for the top tier.',
      bodyBonusAchieved: '{{earnedLabel}} earned this week, the top tier.',
      actionLabel: 'View progress'
    };
  }

  if (context.display.format === 'weekly_timeback_credit') {
    return {
      title: context.promoName || 'Weekly Timeback',
      bodyNotQualified: '{{remainingLabel}} left to reach the next Timeback tier.',
      bodyQualified: "You've reached {{earnedLabel}}. View details for your Timeback progress.",
      bodyBonusAchieved: "You've reached {{earnedLabel}}. View details for your Timeback progress.",
      actionLabel: 'View progress'
    };
  }

  return {
    title: context.promoName || 'Weekly Promo',
    bodyNotQualified: 'You have {{remainingLabel}} left. Keep playing to unlock your reward.',
    bodyQualified: "You've earned {{earnedLabel}}. View details for what's next.",
    bodyBonusAchieved: "You've earned {{earnedLabel}}. View details for what's next.",
    actionLabel: 'View progress'
  };
};

export type PromoDetailsCopy = {
  titleOverride: string;
  metricLabel: string;
  rewardSummaryLabel: string;
  tierSectionTitle: string;
  weeklyMilestonesTitle: string;
  weeklyMilestonesHint: string;
  tierQualifiedLabel: string;
  tierOpenLabel: string;
  emptyTiersLabel: string;
  /** Small print under the details, e.g. when cash is credited. Empty hides it. */
  finePrint: string;
  /** Heading over the tier amounts, e.g. "Total". Empty hides it. */
  tierValueColumnLabel: string;
};

export type PromoHybridDetailsCopy = {
  headerTitle: string;
  progressStatusMessage: string;
  bonusStackHint: string;
  tournamentMessage: string;
  termsAndConditions: string;
};

export function getPromoDetailsCopy(
  context: PromoMessageContext,
  tierSectionTitle: string
): PromoDetailsCopy {
  const defaults = {
    titleOverride: context.promoName,
    metricLabel: context.display.metricLabel,
    rewardSummaryLabel:
      context.display.format === 'weekly_timeback_credit'
        ? 'Current Refund'
        : context.display.format === 'weekly_cash_back'
        ? 'Your Earnings This Month'
        : 'Current Reward',
    tierSectionTitle,
    weeklyMilestonesTitle: 'Weekly Milestones',
    weeklyMilestonesHint: "Don't miss a week - missed weeks do not earn that week's reward.",
    tierQualifiedLabel: 'Qualified',
    tierOpenLabel: 'Open',
    emptyTiersLabel: 'No achievement tiers configured.',
    // Cash back: say when the money lands. The promo job credits it within minutes; the
    // player is promised 1 business day, matching the terms (Jason, 2026-10-28).
    finePrint:
      context.display.format === 'weekly_cash_back'
        ? 'Cash goes to your balance within 1 business day of reaching each tier.'
        : '',
    // Heads the tier amounts: each one is the week's total, not an addition (demo 2026-10-28).
    tierValueColumnLabel: context.display.format === 'weekly_cash_back' ? 'Total' : ''
  };

  return applyConfiguredCopy(
    defaults,
    context.playerFacingConfig?.copy?.detailsModal,
    tokenContext(context)
  );
}

export function getPromoHybridDetailsCopy(context: PromoMessageContext): PromoHybridDetailsCopy {
  const configured = context.playerFacingConfig?.copy?.detailsModal || {};
  const state = context.display.hybridDetails?.state || 'notQualified';
  const defaults = {
    headerTitle: '{{promoBadgeLabel}} Freeroll Progress',
    progressStatusNotQualified: 'Keep going!',
    progressStatusQualified: "You're in!",
    progressStatusBonusAchieved: 'Bonus stack earned!',
    bonusStackHint: "Don't miss a week - you'll miss out on a bonus stack!",
    tournamentMessageNotQualified:
      'Play {{weeklyTargetHours}} this week to earn your {{seatLabel}} seat.',
    tournamentMessageQualified:
      "Congrats, you earned a {{seatLabel}} seat! Here's the scoop on your stacks.",
    tournamentMessageBonusAchieved:
      "You've earned {{bonusStack}} in bonus chips. Keep building your stack."
  };
  const tokens = tokenContext(context);
  const statusField =
    state === 'bonusAchieved'
      ? 'progressStatusBonusAchieved'
      : state === 'qualified'
      ? 'progressStatusQualified'
      : 'progressStatusNotQualified';
  const messageField =
    state === 'bonusAchieved'
      ? 'tournamentMessageBonusAchieved'
      : state === 'qualified'
      ? 'tournamentMessageQualified'
      : 'tournamentMessageNotQualified';

  return {
    headerTitle: interpolate(configured.headerTitle || defaults.headerTitle, tokens),
    progressStatusMessage: interpolate(configured[statusField] || defaults[statusField], tokens),
    bonusStackHint: interpolate(configured.bonusStackHint || defaults.bonusStackHint, tokens),
    tournamentMessage: interpolate(configured[messageField] || defaults[messageField], tokens),
    termsAndConditions: interpolate(
      context.playerFacingConfig?.termsAndConditions || defaultFooter,
      tokens
    )
  };
}

const tokenContext = (context: PromoMessageContext) => ({
  promoName: context.promoName,
  formatLabel: context.display.formatLabel,
  remainingLabel: context.display.hybridDetails?.remainingLabel || context.remainingLabel,
  progressLabel: context.display.progressLabel,
  earnedLabel: context.display.earnedLabel,
  statusLabel: context.display.statusLabel,
  promoBadgeLabel:
    context.playerFacingConfig?.copy?.inlineBar?.promoBadgeLabel ||
    (context.display.format === 'hybrid_freeroll_qualification'
      ? '$100K'
      : context.display.format === 'weekly_cash_back' &&
        context.display.earnedLabel === 'Not qualified'
      ? 'Cash Back'
      : context.display.earnedLabel),
  currentWeekHours: context.display.hybridDetails?.currentWeekHoursLabel || '',
  weeklyTargetHours: context.display.hybridDetails?.weeklyTargetHoursLabel || '',
  totalHours: context.display.hybridDetails?.totalHoursLabel || '',
  totalTargetHours: context.display.hybridDetails?.totalTargetHoursLabel || '',
  eligibleBlinds:
    context.display.hybridDetails?.eligibleBlindsLabel || context.display.eligibleBlindsLabel || '',
  weekWindowLabel:
    context.display.hybridDetails?.weekWindowLabel || context.display.weekWindowLabel || '',
  resetDayLabel: context.display.hybridDetails?.resetDayLabel || context.display.resetDayLabel,
  tournamentDate: context.display.hybridDetails?.tournamentDateLabel || '',
  seatLabel: context.display.hybridDetails?.seatLabel || '',
  startingStack: context.display.hybridDetails?.startingStackLabel || '',
  bonusStack: context.display.hybridDetails?.bonusStackLabel || '',
  qualifyingDateRange:
    context.display.hybridDetails?.qualifyingDateRangeLabel ||
    context.display.qualifyingDateRangeLabel ||
    ''
});

const applyConfiguredCopy = <T extends Record<string, string>>(
  defaults: T,
  configured: Record<string, string> | undefined,
  tokens: Record<string, string>
): T => {
  return Object.entries(defaults).reduce<T>(
    (copy, [key, fallback]) => {
      const template = configured?.[key] || fallback;
      copy[key as keyof T] = interpolate(template, tokens) as T[keyof T];
      return copy;
    },
    { ...defaults }
  );
};

const interpolate = (template: string, tokens: Record<string, string>) =>
  template.replace(/{{\s*([A-Za-z0-9_]+)\s*}}/g, (_, token) => tokens[token] ?? '');
